import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { allowedEventRoles, EventRole, LiveEvent, User } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import {
    Badge,
    Flex,
    Pagination,
    ScrollArea,
    Select,
    Skeleton,
    Stack,
    Table,
    Text,
    Tooltip,
} from "@mantine/core";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import ConductionModal from "../ConductionModal";

interface LiveEventUsersBrowserProps {
    liveEvent: LiveEvent;
    onClose: () => void;
    defaultRoleFilter?: EventRole;
}

const LiveEventUsersBrowser: React.FC<LiveEventUsersBrowserProps> = ({ liveEvent, onClose, defaultRoleFilter }) => {
    const [participants, setParticipants] = useState<User[] | null>(null);
    const [eventAdmins, setEventAdmins] = useState<User[] | null>(null);
    const [instrumentalists, setInstrumentalists] = useState<User[] | null>(null);
    const [desiredPage, setDesiredPage] = useState<number>(1);
    const [usersPerPage, setUsersPerPage] = useState<number>(6);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedRoleFilter, setSelectedRoleFilter] = useState<EventRole | "">(defaultRoleFilter ?? "");

    const auth = useContext(AuthContext);
    if (auth === undefined) {
        throw new Error("EventUserBrowser must be a child of AuthContext.");
    }
    const token = auth.token;
    if (token === null) {
        return;
    }

    const height = 60; // view height (vh)
    const calculateUsersPerPage = useCallback(() => {
        const rowHeight = 45; // pixel estimation
        const containerHeight = window.innerHeight * (height / 100) - 100; // Use 60% of the viewport height minus some estimate for other elements
        setUsersPerPage(Math.floor(containerHeight / rowHeight));

        const userCount = (selectedRoleFilter == null || selectedRoleFilter === "Participant" ? participants?.length || 0 : 0)
            + (selectedRoleFilter == null || selectedRoleFilter === "EventAdmin" ? eventAdmins?.length || 0 : 0)
            + (selectedRoleFilter == null || selectedRoleFilter === "Instrumentalist" ? instrumentalists?.length || 0 : 0);

        if (Math.floor(containerHeight / rowHeight) * (desiredPage - 1) + 1 > userCount) {
            setDesiredPage(1);
        }
    }, [desiredPage, window.innerHeight, selectedRoleFilter]);

    useEffect(() => {
        calculateUsersPerPage();
        window.addEventListener("resize", calculateUsersPerPage);
        return () => window.removeEventListener("resize", calculateUsersPerPage);
    }, [calculateUsersPerPage]);

    const fetchLiveEvent = () => {
        const notificationId = Utils.displayNotification(
            "Loading Users",
            "Fetching user list, please wait...",
            "blue",
            "loading"
        );

        ConductionAPI.getLiveEvent(liveEvent.id, token)
            .then((liveEvent: LiveEvent | null) => {
                if (liveEvent === null) {
                    setParticipants(null);
                    setEventAdmins(null);
                    setInstrumentalists(null);
                    Utils.updateNotification(
                        notificationId,
                        "Error Fetching Users",
                        "An error occurred while retrieving users.",
                        "red",
                        <IconAlertCircle size={16} />,
                        true
                    );
                } else {
                    setParticipants(liveEvent.getParticipants() ?? []);
                    setEventAdmins(liveEvent.getEventAdmins() ?? []);
                    setInstrumentalists(liveEvent.getInstrumentalists() ?? []);

                    Utils.updateNotification(
                        notificationId,
                        "Users Loaded",
                        undefined,
                        "green",
                        <IconCheck size={16} />
                    );
                }
            })
            .catch(() => {
                Utils.updateNotification(
                    notificationId,
                    "Error Fetching Users",
                    "An error occurred while retrieving users.",
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            })
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        fetchLiveEvent();
    }, []);

    const allFilteredUsers = useMemo(() => {
        let allUsers = selectedRoleFilter == "" || selectedRoleFilter === "Participant" ? participants ?? [] : [];
        allUsers = allUsers.concat(selectedRoleFilter == "" || selectedRoleFilter === "EventAdmin" ? eventAdmins ?? [] : [])
        allUsers = allUsers.concat(selectedRoleFilter == "" || selectedRoleFilter === "Instrumentalist" ? instrumentalists ?? [] : []);
        return allUsers;
    }, [participants, eventAdmins, instrumentalists, selectedRoleFilter]);

    const paginatedUsers = useMemo(() => {
        const start = (desiredPage - 1) * usersPerPage;
        return allFilteredUsers?.slice(start, start + usersPerPage) ?? [];
    }, [desiredPage, allFilteredUsers, usersPerPage]);

    return (
        <ConductionModal
            title={`Users for Live Event: ${liveEvent.name}`}
            onClose={onClose}
            size={"lg"}
            opened={true}
        >
            <Flex
                direction={"column"}
                align="center"
                justify="space-between"
                style={{
                    height: `${height}vh`
                }}
            >
                {loading ? (
                    <Skeleton height={"100%"} radius="md" />
                ) : (
                    <Stack>
                        <Tooltip
                            label='Filter Users by Role'
                            withArrow
                        >
                            <Select
                                data={[
                                    { value: "", label: 'All' },
                                    ...allowedEventRoles.map((role) => ({
                                        value: role,
                                        label: role,
                                    })),
                                ]}
                                value={selectedRoleFilter}
                                onChange={(value: string | null) => setSelectedRoleFilter(value as EventRole | "")}
                                placeholder="Event Role"
                                clearable={false}
                                size="xs"
                                style={{
                                    marginLeft: 0,
                                    marginRight: 0,
                                    minWidth: 120
                                }}
                                styles={{
                                    input: {
                                        minHeight: 20,
                                        height: 20
                                    }
                                }}
                            />
                        </Tooltip>
                        {(allFilteredUsers === null || allFilteredUsers.length === 0) ? (
                            <Text>No users found.</Text>
                        ) : (
                            <ScrollArea
                                style={{
                                    width: "100%"
                                }}
                            >
                                <Table
                                    striped
                                    stickyHeader
                                    highlightOnHover
                                    width={"100%"}
                                >
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Username</Table.Th>
                                            <Table.Th>Event Role</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {paginatedUsers.map((user) => {
                                            const participant = participants !== null && participants.some((u) => u.id === user.id);
                                            const eventAdmin = eventAdmins !== null && eventAdmins.some((u) => u.id === user.id);
                                            const instrumentalist = instrumentalists !== null && instrumentalists.some((u) => u.id === user.id);
                                            return (
                                                <Table.Tr key={user.username}>
                                                    <Table.Td>{user.username}</Table.Td>
                                                    <Table.Td>
                                                        {(participant) && (
                                                            <Badge color="blue">
                                                                Participant
                                                            </Badge>
                                                        )}
                                                        {(eventAdmin) && (
                                                            <Badge color="yellow">
                                                                Event Admin
                                                            </Badge>
                                                        )}
                                                        {(instrumentalist) && (
                                                            <Badge color="darkGreen">
                                                                Instrumentalist
                                                            </Badge>
                                                        )}
                                                        {(!participant && !eventAdmin && !instrumentalist) && (
                                                            <Badge color="red">
                                                                Other
                                                            </Badge>
                                                        )}
                                                    </Table.Td>
                                                </Table.Tr>
                                            );
                                        })}
                                    </Table.Tbody>
                                </Table>
                            </ScrollArea>
                        )}
                    </Stack>
                )}

                {allFilteredUsers && allFilteredUsers.length > usersPerPage && (
                    <Pagination
                        total={Math.ceil(allFilteredUsers.length / usersPerPage)}
                        value={desiredPage}
                        onChange={setDesiredPage}
                    />
                )}
            </Flex>
        </ConductionModal>
    );
};

export default LiveEventUsersBrowser;
